/**
 * Pure helpers for Docker auto-discovery (Workstream B3): the analytics count
 * bucket, duplicate detection against saved connections, and building a Jam
 * `Connection` from a discovered candidate. Kept dependency-free so they are
 * cheap to unit-test.
 */
import { v4 as uuidv4 } from 'uuid';
import type { Connection } from '../../../types/electron';
import type { AzureStorageEndpoints, StorageService } from '../../../types/azureStorage';
import type { DockerAzuriteDetails, DockerCandidate } from '../../../types/dockerDiscovery';

/** Stable identity of an Azurite candidate's endpoint map (order-independent). */
function endpointsKey(endpoints: AzureStorageEndpoints | undefined): string {
  return JSON.stringify(Object.entries(endpoints ?? {}).filter(([, value]) => Boolean(value)).sort());
}

/**
 * Azurite duplicates are matched on engine + account + endpoint set, NEVER on
 * `port`: that field holds only the first of three published service ports, so
 * two containers publishing different sets are distinct connections while the
 * same container re-detected is one (spec §4.1).
 */
function findDuplicateAzuriteConnection(
  details: DockerAzuriteDetails,
  existing: Connection[]
): Connection | undefined {
  const endpoints = endpointsKey(details.endpoints);
  return existing.find(
    (c) =>
      c.engine === 'azurestorage' &&
      String(c.server ?? '').toLowerCase() === details.account.toLowerCase() &&
      endpointsKey(c.azureStorageEndpoints) === endpoints
  );
}

/** Exactly the services this container published (Azurite has no Files service). */
function publishedServices(details: DockerAzuriteDetails): StorageService[] {
  const order: Array<keyof DockerAzuriteDetails['endpoints']> = ['blob', 'queue', 'table'];
  return order.filter((service) => Boolean(details.endpoints[service]));
}

/** Low-cardinality bucket for the detected-candidate count (analytics). */
export function countBucket(count: number): '0' | '1' | '2-3' | '4+' {
  if (count <= 0) return '0';
  if (count === 1) return '1';
  if (count <= 3) return '2-3';
  return '4+';
}

/**
 * Find the saved connection a candidate duplicates — matched on
 * engine + host + port + user (docker container hosts are all 127.0.0.1, so
 * the port is the distinguishing field; local candidates use a unique server
 * string — e.g. `localhost\SQLEXPRESS` — with no port, so port is dropped
 * from the match entirely for `kind: 'local'`. This also sidesteps the two
 * different "no port" spellings in play: native discovery emits `''`
 * (`mapLocalInstanceToCandidate`) while a manually-saved Windows-auth MSSQL
 * connection stores the form's `'<auto>'` placeholder — comparing those
 * literally would miss an already-saved instance and re-offer it as addable).
 * Returned so the dialog can name it in the disabled-Add tooltip; `undefined`
 * when the candidate is new.
 */
export function findDuplicateDockerConnection(
  candidate: DockerCandidate,
  existing: Connection[]
): Connection | undefined {
  if (candidate.azureStorage) {
    return findDuplicateAzuriteConnection(candidate.azureStorage, existing);
  }
  const host = candidate.host.toLowerCase();
  if (candidate.kind === 'local') {
    return existing.find(
      (c) =>
        (c.engine ?? 'mssql') === candidate.engine &&
        String(c.server ?? '').toLowerCase() === host &&
        (c.authentication ?? '') === candidate.authentication
    );
  }
  const port = String(candidate.port);
  const user = (candidate.user ?? '').toLowerCase();
  return existing.find(
    (c) =>
      (c.engine ?? 'mssql') === candidate.engine &&
      String(c.server ?? '').toLowerCase() === host &&
      String(c.port ?? '') === port &&
      String(c.login ?? '').toLowerCase() === user
  );
}

/**
 * Build a saved `Connection` from a discovered candidate. The password is
 * included only when the container env carried one; otherwise the general
 * on-connect prompt (Workstream B2) collects it on first Connect.
 */
export function buildConnectionFromDockerCandidate(candidate: DockerCandidate): Connection {
  if (candidate.azureStorage) {
    const details = candidate.azureStorage;
    const connection: Connection = {
      id: uuidv4(),
      engine: 'azurestorage',
      alias: candidate.containerName,
      // The account name is the server for this engine; the endpoints carry the
      // host:port of each published service (spec §4.2).
      server: details.account,
      port: '0',
      authentication: 'Account Key',
      readOnly: false,
      azureStorageEndpoints: { ...details.endpoints },
      azureStorageServices: publishedServices(details),
      azureStorageScope: { kind: 'account' },
      azureStorageEmulator: true,
    };
    if (candidate.password) connection.password = candidate.password;
    return connection;
  }

  const connection: Connection = {
    id: uuidv4(),
    engine: candidate.engine,
    alias: candidate.containerName,
    server: candidate.host,
    port: candidate.port,
    authentication: candidate.authentication,
    login: candidate.user,
    readOnly: false,
  };
  if (candidate.database) connection.database = candidate.database;
  if (candidate.oracleServiceName) connection.oracleServiceName = candidate.oracleServiceName;
  if (candidate.password) connection.password = candidate.password;
  if (candidate.engine === 'mssql') {
    // Parity with the manual form default (connectionDefaultValues): local and
    // containerized SQL Server certs are self-signed, and Add saves the
    // connection without ever showing the "Trust server certificate" checkbox.
    connection.trustServerCertificate = true;
  }
  return connection;
}
